import type { ExecucaoNaConversa, Fluxo, ResumoFluxo, SalvarFluxo } from "@ydchat/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, ErroApi } from "@/lib/api";

const erro = (e: unknown, padrao: string) => toast.error(e instanceof ErroApi ? e.message : padrao);

export const useFluxos = () =>
  useQuery({ queryKey: ["fluxos"], queryFn: () => api.get<ResumoFluxo[]>("/fluxos") });

export const useFluxo = (id: string) =>
  useQuery({ queryKey: ["fluxos", id], queryFn: () => api.get<Fluxo>(`/fluxos/${id}`) });

export function useAcoesFluxo(id: string) {
  const qc = useQueryClient();
  const aplicar = (f: Fluxo) => {
    qc.setQueryData(["fluxos", id], f);
    void qc.invalidateQueries({ queryKey: ["fluxos"], exact: true });
  };
  return {
    salvar: useMutation({
      mutationFn: (d: SalvarFluxo) => api.put<Fluxo>(`/fluxos/${id}`, d),
      onSuccess: aplicar,
      onError: (e) => erro(e, "Não foi possível salvar."),
    }),
    publicar: useMutation({
      mutationFn: () => api.post<Fluxo>(`/fluxos/${id}/publicar`),
      onSuccess: (f) => {
        aplicar(f);
        toast.success(`Versão ${f.versaoPublicada} publicada.`);
      },
      onError: (e) => erro(e, "Não foi possível publicar."),
    }),
    ativar: useMutation({
      mutationFn: (ativo: boolean) => api.put<Fluxo>(`/fluxos/${id}/ativo`, { ativo }),
      onSuccess: (f) => {
        aplicar(f);
        toast.success(f.ativo ? "Fluxo ligado." : "Fluxo desligado.");
      },
      onError: (e) => erro(e, "Não foi possível mudar."),
    }),
    excluir: useMutation({
      mutationFn: () => api.del(`/fluxos/${id}`),
      onSuccess: () => void qc.invalidateQueries({ queryKey: ["fluxos"] }),
      onError: (e) => erro(e, "Não foi possível excluir."),
    }),
  };
}

export const useFluxoDaConversa = (conversaId: string) =>
  useQuery({
    queryKey: ["conversa", conversaId, "fluxo"],
    queryFn: () =>
      api.get<{ ativo: ExecucaoNaConversa | null; manuais: { id: string; nome: string }[] }>(
        `/conversas/${conversaId}/fluxo`,
      ),
    staleTime: 10_000,
    // O fluxo anda com as mensagens da cliente: confere de tempos em tempos.
    refetchInterval: 15_000,
  });

export function useAcoesFluxoConversa(conversaId: string) {
  const qc = useQueryClient();
  const atualizar = () => {
    void qc.invalidateQueries({ queryKey: ["conversa", conversaId] });
    void qc.invalidateQueries({ queryKey: ["conversas"] });
  };
  return {
    iniciar: useMutation({
      mutationFn: (fluxoId: string) =>
        api.post(`/conversas/${conversaId}/fluxo/iniciar`, { fluxoId }),
      onSuccess: () => {
        toast.success("Fluxo iniciado.");
        atualizar();
      },
      onError: (e) => erro(e, "Não foi possível iniciar."),
    }),
    assumir: useMutation({
      mutationFn: () => api.post(`/conversas/${conversaId}/fluxo/assumir`),
      onSuccess: atualizar,
      onError: (e) => erro(e, "Não foi possível interromper."),
    }),
  };
}
